/**
 * locker-ep-first.js — правило «спершу посилки Епіцентру» (рішення власника
 * 20.10.2026), спільне для ОБОХ реалізацій поштоматного флоу (`app_min1.html`
 * + `app.js` і `courier.html` + `courier/sections/locker.js`).
 *
 * Правило. На поштоматі мережі Епіцентру, поки в списку на вкладення є
 * НЕопрацьовані посилки Епіцентру, комірка для будь-якої іншої посилки не
 * відкривається — ні тапом по посилці, ні скануванням, ні «Відкрити повторно»,
 * ні вибором розміру. Посилка Епіцентру опрацьована в цьому візиті, коли її
 * вкладено (підтверджено) або по ній подано «Проблему» (будь-якого типу) на
 * цьому екрані.
 *
 * Дві різні осі «Епіцентру» (див. locker-backend/app/utils/locker_networks.py):
 *  • мережа ТОЧКИ — `networkId === 'epicentr'` з /locker/by-qr (чий поштомат).
 *    Саме вона вмикає правило. Невідома мережа — правило НЕ діє: краще
 *    пропустити блок, ніж заблокувати курʼєра на чужому поштоматі;
 *  • агент ПОСИЛКИ — рівно «Епіцентр онлайн_поштомати» (точний збіг, як
 *    isEpAgent у flows.js). Він визначає, які посилки йдуть першими.
 * На поштоматах Meest, Rozetka та інших посилки Епіцентру лише стоять першими
 * у списку, як і раніше, без жодного блоку.
 *
 * Модуль навмисно ЛИСТКОВИЙ — без жодного імпорту (той самий прийом, що й
 * locker-cells.js і locker-marks.js): якби кожен екран ніс свою копію правила,
 * вони розійшлись би тихо, і на одному екрані блок діяв би, а на іншому ні.
 */

/** Мережа поштомата з /locker/by-qr, на якій діє правило. */
export const EPICENTR_NETWORK_ID = 'epicentr';

/** Агент (відправник) посилки Епіцентру — значення 1С, точний збіг. */
export const EPICENTR_PARCEL_AGENT = 'Епіцентр онлайн_поштомати';

/**
 * Розмір, з яким відкриваємо посилку Епіцентру одразу після скану чи тапу.
 *
 * Під посилку Епіцентру в поштоматі вже заброньовано комірку, і 1С рахує її
 * зайнятою: у повному поштоматі вільних «0» усіх розмірів, хоча бронь чекає.
 * Перевіряти вільні для неї не можна — це блокувало вкладення (30.10, поштомат
 * 8476: 8 посилок із бронню, 4 дні жодного вкладення). Відкриваємо найменшим:
 * розмір, не більший за бронь, 1С відкриває заброньовану комірку, а більший
 * трактує як зміну розміру (13 / 20 «більшого розміру»). На проді 27–30.10
 * перша спроба з XS вдалась у 759 із 767 посилок, з XL — у 284 із 329.
 */
export const EPICENTR_BOOKED_OPEN_SIZE = 'XS';

/** Чи це поштомат мережі Епіцентру. null / 'unknown' / будь-що інше — ні. */
export function isEpicentrNetwork(networkId) {
  try {
    return String(networkId || '').trim().toLowerCase() === EPICENTR_NETWORK_ID;
  } catch {
    return false;
  }
}

/**
 * Чи це посилка Епіцентру. Точний збіг агента (без урахування регістру і
 * крайніх пробілів): клієнт «Шкуратовський Артем Епіцентр» — НЕ Епіцентр.
 */
export function isEpicentrParcel(parcel) {
  try {
    const agent = parcel && parcel.agent;
    if (!agent) return false;
    return String(agent).trim().toLowerCase() === EPICENTR_PARCEL_AGENT.toLowerCase();
  } catch {
    return false;
  }
}

function hasId(handledIds, id) {
  if (!handledIds || id === null || id === undefined) return false;
  if (typeof handledIds.has === 'function') return handledIds.has(id);
  if (Array.isArray(handledIds)) return handledIds.includes(id);
  return false;
}

/**
 * Невкладені й без «Проблеми» посилки Епіцентру зі списку на вкладення —
 * у порядку списку. `handledIds` — Set або масив parcelID, опрацьованих у
 * цьому візиті.
 */
export function pendingEpicentrParcels(delivery, handledIds) {
  if (!Array.isArray(delivery)) return [];
  return delivery.filter((p) => p && isEpicentrParcel(p) && !hasId(handledIds, p.parcelID));
}

/**
 * Чи можна відкривати комірку для посилки `parcel`.
 *
 * null — можна (не поштомат Епіцентру, сама посилка Епіцентру, або посилок
 * Епіцентру, що чекають, немає). Інакше `{ count }` — скільки посилок
 * Епіцентру ще треба вкласти чи позначити «Проблемою».
 *
 * Агент беремо і з самої посилки, і з її запису в списку: у поточній посилці
 * екрана агента може не бути (скан), а посилку Епіцентру блокувати не можна
 * ніколи — інакше курʼєр не вийде з блоку взагалі.
 */
export function epicentrBlockFor(parcel, delivery, handledIds, networkId) {
  if (!isEpicentrNetwork(networkId)) return null;
  if (!parcel) return null;
  if (isEpicentrParcel(parcel)) return null;
  const list = Array.isArray(delivery) ? delivery : [];
  const own = parcel.parcelID !== undefined && parcel.parcelID !== null
    ? list.find((p) => p && p.parcelID === parcel.parcelID)
    : null;
  if (own && isEpicentrParcel(own)) return null;
  const pending = pendingEpicentrParcels(list, handledIds);
  return pending.length ? { count: pending.length } : null;
}

/**
 * Стан правила для малювання списку: `active` — блок діє зараз; `pendingIds`
 * — посилки Епіцентру, які треба підсвітити.
 */
export function epFirstListView(delivery, handledIds, networkId) {
  if (!isEpicentrNetwork(networkId)) return { active: false, count: 0, pendingIds: new Set() };
  const pending = pendingEpicentrParcels(delivery, handledIds);
  return {
    active: pending.length > 0,
    count: pending.length,
    pendingIds: new Set(pending.map((p) => p.parcelID)),
  };
}

// ─── Опрацьовані за візит ───────────────────────────────────────────────────
// Візит = поштомат (branchRef). Окремо від списку посилок, бо список
// перезавантажується (нове сканування QR того ж поштомата), а 1С ще якийсь час
// може віддавати і вкладену посилку, і посилку з «Проблемою» — забувати їх не
// можна, інакше курʼєр знову впреться в блок. Інший поштомат — свій запис.

/** Порожнє сховище візитів: Map(branchRef → { inserted: Set, problem: Set }). */
export function createEpFirstVisits() {
  return new Map();
}

function visitOf(visits, branchRef, create) {
  if (!(visits instanceof Map)) return null;
  const key = String(branchRef || '');
  let visit = visits.get(key);
  if (!visit && create) {
    visit = { inserted: new Set(), problem: new Set() };
    visits.set(key, visit);
  }
  return visit || null;
}

/**
 * Позначити посилку опрацьованою в цьому візиті: `how` — 'inserted'
 * (вкладення підтверджено) або 'problem' (подано «Проблему»).
 */
export function noteHandledInVisit(visits, branchRef, parcelID, how) {
  if (parcelID === null || parcelID === undefined || parcelID === '') return false;
  const visit = visitOf(visits, branchRef, true);
  if (!visit) return false;
  (how === 'problem' ? visit.problem : visit.inserted).add(parcelID);
  return true;
}

/**
 * «Undo» після «Проблеми»: посилка знову чекає. Позначку вкладення не
 * знімаємо — вкладену посилку назад не дістати.
 */
export function forgetProblemInVisit(visits, branchRef, parcelID) {
  const visit = visitOf(visits, branchRef, false);
  if (!visit) return false;
  return visit.problem.delete(parcelID);
}

/** parcelID, опрацьовані в цьому візиті (вкладені + з «Проблемою»). */
export function handledInVisit(visits, branchRef) {
  const visit = visitOf(visits, branchRef, false);
  if (!visit) return new Set();
  return new Set([...visit.inserted, ...visit.problem]);
}

// ─── Памʼять візиту поза сторінкою ──────────────────────────────────────────
// Памʼяті сторінки мало: застосунок сам перезавантажує її («Спробувати знову»
// на блокері звʼязку й на помилці Meest), а Back у Telegram веде до маршруту
// й назад. Після нового скану 1С знову віддає посилку з «Проблемою» — і без
// памʼяті курʼєр знову впирався б у блок і подавав «Проблему» вдруге (новий
// запис у нас і нова нотатка в Meest: дедуп бекенда ловить лише 120 с).
// Тому два джерела, і обидва лише ДОДАЮТЬ опрацьовані:
//  • сервер — /locker/parcels віддає insertedParcelIds / problemParcelIds:
//    що цей курʼєр сьогодні вклав і по чому подав «Проблему» на цій точці;
//  • localStorage до кінця київської доби — коли сервер не знає (старий
//    бекенд, збій обліку). Інший курʼєр чи інша доба — записи не наші.

/** Префікс ключа localStorage; повний ключ — окремий на кожен екран. */
export const EP_FIRST_STORAGE_PREFIX = 'locker.epFirst.v1';

/** Стеля на кількість parcelID одного візиту — і в сховищі, і з сервера. */
const MAX_IDS_PER_VISIT = 500;

/** Ключ localStorage екрана (`client` — ідентифікатор екрана з locker-marks.js). */
export function epFirstStorageKey(client) {
  return `${EP_FIRST_STORAGE_PREFIX}:${client || 'unknown'}`;
}

/** Київська доба 'РРРР-ММ-ДД' — строк життя записів у localStorage. */
export function kyivDay(now) {
  const d = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(d);
    const part = (type) => (parts.find((p) => p.type === type) || {}).value;
    const y = part('year');
    const m = part('month');
    const day = part('day');
    if (y && m && day) return `${y}-${m}-${day}`;
  } catch {
    // без ICU — доба за UTC: гірше на 2–3 години біля півночі, але не падає
  }
  return d.toISOString().slice(0, 10);
}

/** localStorage або null: доступ кидає виняток у приватному вікні / без даних сайту. */
export function browserStorage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

function idList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((x) => (typeof x === 'string' && x !== '') || (typeof x === 'number' && Number.isFinite(x)))
    .slice(0, MAX_IDS_PER_VISIT);
}

/**
 * Візити з localStorage. `owner` — `{ day, courier }`: записи іншої доби чи
 * іншого курʼєра ігноруються. Будь-яка помилка — порожнє сховище, не виняток.
 */
export function restoreEpFirstVisits(storage, key, owner) {
  const visits = createEpFirstVisits();
  try {
    const raw = storage && storage.getItem(key);
    if (!raw) return visits;
    const data = JSON.parse(raw);
    const day = owner && owner.day;
    const courier = String((owner && owner.courier) || '');
    if (!data || !day || data.day !== day || String(data.courier || '') !== courier) return visits;
    const stored = data.visits && typeof data.visits === 'object' ? data.visits : {};
    Object.keys(stored).forEach((ref) => {
      const v = stored[ref];
      if (!ref || !v) return;
      idList(v.inserted).forEach((id) => noteHandledInVisit(visits, ref, id, 'inserted'));
      idList(v.problem).forEach((id) => noteHandledInVisit(visits, ref, id, 'problem'));
    });
    return visits;
  } catch {
    return createEpFirstVisits();
  }
}

/** Записати візити в localStorage (лише непорожні). true — записано. */
export function persistEpFirstVisits(storage, key, visits, owner) {
  if (!storage || !(visits instanceof Map) || !owner || !owner.day) return false;
  try {
    const out = {};
    visits.forEach((v, ref) => {
      if (!ref || !v) return;
      const inserted = idList([...v.inserted]);
      const problem = idList([...v.problem]);
      if (inserted.length || problem.length) out[ref] = { inserted, problem };
    });
    storage.setItem(key, JSON.stringify({ day: owner.day, courier: String(owner.courier || ''), visits: out }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Опрацьовані з відповіді /locker/parcels (`insertedParcelIds`,
 * `problemParcelIds`) — у візит поштомата `branchRef`. Лише додає: сервер не
 * знімає позначок, поставлених на цьому екрані. Повертає, скільки parcelID
 * додалось нових (0 — зберігати нічого).
 */
export function noteServerHandledInVisit(visits, branchRef, data) {
  if (!branchRef || !data || !(visits instanceof Map)) return 0;
  const before = handledInVisit(visits, branchRef);
  let added = 0;
  const take = (ids, how) => idList(ids).forEach((id) => {
    const isNew = !before.has(id);
    if (noteHandledInVisit(visits, branchRef, id, how) && isNew) {
      before.add(id);
      added += 1;
    }
  });
  take(data.insertedParcelIds, 'inserted');
  take(data.problemParcelIds, 'problem');
  return added;
}

/**
 * Тексти для курʼєра. `title` + `message` — плашка, `text` — тост одним
 * рядком, `hint` — підказка над списком, поки блок діє.
 */
export function epFirstText(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  const title = `Спершу вкладіть посилки Епіцентру (${n})`;
  const message = 'Якщо посилки немає або вона не влазить — відкрийте її і натисніть «Проблема».';
  return {
    title,
    message,
    text: `${title}. ${message}`,
    hint: `Спершу — посилки Епіцентру (${n}). Решта відкриється після них.`,
  };
}
